import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import EditTable from "../../../molecules/Editable";
import FlatCard from "../../../atoms/FlatCard";
import Controls from "../../../atoms/Controls";
import { useEffect, useMemo, useState } from "react";
import { useMasterTables, getTherapyId as utilGetTherapyId, getTherapyPrice as utilGetTherapyPrice, resolveTarifGroupForRxType } from "../../../stores/MasterTables";
import { handleParseInput } from "../../../helpers/parseInput";
import { v4 as uuidv4 } from "uuid";
import { QuickRxData, RxType } from "./RxOverView";
import { observer } from "mobx-react";
import WarningDialog from "../../../atoms/WarningDialog";
import { useUserAccounts } from "../../../stores/User/User.hooks";
import notificationStore from "../../../stores/Notification/notificationStore";

// QuickAddRxDialog.tsx
interface QuickAddRxDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  setQuickRxData: React.Dispatch<React.SetStateAction<QuickRxData>>;
  rxData: QuickRxData;
  user: any;
  gridColumns: any[];
  masterData: any;
  initialInputValue?: string;
  isQuickAdd?: boolean;
  // columnSets?: any[];
}
interface RowData {
  id: string;
  [key: string]: any;
}

const QuickAddRxDialog: React.FC<QuickAddRxDialogProps> = observer(
  ({
    open,
    onClose,
    onSave,
    setQuickRxData,
    rxData,
    user,
    gridColumns,
    masterData,
    initialInputValue = "",
    isQuickAdd,
  }) => {
    const accounts = useUserAccounts();

    const accountTherapyType = useMemo(() => {
      if (!rxData.rxAccount || !accounts) return "Physiotherapie";
      const selectedAccount = accounts.find(
        (a: any) => a.name === rxData.rxAccount || a.id === rxData.rxAccount
      );
      return selectedAccount?.therapyType || "Physiotherapie";
    }, [rxData.rxAccount, accounts]);
    //   const [selectedTarifGroup, setSelectedTarifGroup] = useState("");
    //  const [rxTypeData, setRxTypeData] = useState("");
    //   const [rxAccount, setRxAccount] = useState("");
    //  const [tableData, setTableData] = useState<RowData[]>([{ id: uuidv4() }]); // Initialize with an empty array
    const [savingRx, setSavingRx] = useState(false);
    const [tarifGroupOptions, setTarifGroupOptions] = useState<string[]>([]);
  const { data: masterTablesData } = useMasterTables();
  // Local unsaved changes tracking
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);
  const getTherapyId = (tarifGroup: string, abbr: string) => utilGetTherapyId(masterTablesData || [], tarifGroup, abbr);
  const getTherapyPrice = (tarifGroup: string, abbr: string) => utilGetTherapyPrice(masterTablesData || [], tarifGroup, abbr);

    const handleRxTypeChange = (event: any) => {
      const rxTypeId = event.target.id || event.target.value;
      // Update quickRxData immediately
      setQuickRxData((prev) => ({
        ...prev,
        rxTypeData: { id: event.target.value, text: event.target.value },
      }));

      // Shared rule, so quick-add agrees with the full Rx forms. It used to
      // take `matchedTarifGroups[0]` unconditionally, which silently billed a
      // Kasse Rx as GKV instead of asking between GKV and Post — and wrote
      // `undefined` for a type that maps to no group at all.
      const { options, nextTarifGroupId, requiresChoice } = resolveTarifGroupForRxType(
        masterTablesData || [],
        rxTypeId,
        rxData.selectedTarifGroup,
        accountTherapyType
      );
      setTarifGroupOptions(options);

      setQuickRxData((prev) => ({
        ...prev,
        selectedTarifGroup: nextTarifGroupId,
      }));

      if (requiresChoice) {
        notificationStore.showMessage(
          "Bitte wählen Sie eine Tarifgruppe für den gewählten Rezept-Typ.",
          "warning"
        );
      }
    };

    const handleTarifGroupChange = (e: any) => {
      const selectedValue = e.target.value;
      setQuickRxData((prev) => ({
        ...prev,
        selectedTarifGroup: selectedValue,
      }));
    };
    const handleRxAccountChange = (event: any) => {
      setQuickRxData((prev) => ({
        ...prev,
        rxAccount: event.target.id,
      }));
    };

    const handleSave = () => {
      setSavingRx(true);
      onSave();
      setUnsavedChanges(false);
      setSavingRx(false);
    };

    // For table data changes
    const handleTableDataChange = (rows: any[]) => {
      setQuickRxData((prev) => ({
        ...prev,
        tableData: rows,
      }));
    };

    useEffect(() => {
      setUnsavedChanges(true);
    }, [rxData]);

    const rxType = (masterTablesData || [])
      .filter((obj: any) => "rxType" in obj)
      .map((obj: any) => obj.rxType);
    const handleCloseEditDialog = () => {
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onClose();
      }
    };

    const handleUnsavedChangesWarning = () => {
      handleSave();
    };

    const handleCloseUnsavedChangesWarning = () => {
      onClose();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    return (
      <>
        <Dialog open={open} onClose={handleCloseEditDialog}>
          <DialogTitle>Neues Rezept erstellen</DialogTitle>
          <DialogContent sx={{ width: "500px" }}>
            <Controls.Select
              name="rxtype"
              label="Rezept-typ"
              value={rxData.rxTypeData.id}
              onChange={handleRxTypeChange}
              options={rxType[0]?.map((option: any) => ({
                label: option.text.toString(),
                value: option.text.toString(),
                id: option.id.toString(),
              }))}
              required
            />
            <Controls.Select
              name="tarifGroup"
              label="Tarif Group"
              value={rxData.selectedTarifGroup}
              onChange={handleTarifGroupChange}
              options={tarifGroupOptions.map((group) => ({
                label: group,
                value: group,
              }))}
              required
            />
            <Controls.Select
              name="accountId"
              label="Mandant"
              id={1}
              value={rxData.rxAccount}
              onChange={handleRxAccountChange}
              options={accounts?.map((option: any) => ({
                label: option.name.toString(),
                value: option.name.toString(),
                id: option.id.toString(),
              }))}
              required
            />
            <FlatCard title="Heilmittel">
              {!rxData.selectedTarifGroup && (
                <Typography>Bitte wählen Sie Rx-Typ und Tarifgruppe, um fortzufahren</Typography>
              )}
              {rxData.selectedTarifGroup && (
                <EditTable
                  initialColumns={gridColumns}
                  initialRows={rxData.tableData}
                  // columnSets={columnSets}
                  onTableDataChange={handleTableDataChange}
                  parseInput={handleParseInput}
                  // handleAddColumnSet={handleAddColumnSet}
                  masterTablesData={masterData}
                  isQuickAdd={isQuickAdd}
                  quickAddValue={initialInputValue}
                  isTreatmentRows
                  selectedRx={{
                    rxtype: rxData.rxTypeData,
                    tarifGroupId: rxData.selectedTarifGroup,
                    therapyType: accountTherapyType,
                  }}
                />
              )}
            </FlatCard>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={handleCloseEditDialog}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={savingRx}>
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}
      </>
    );
  }
);

export default QuickAddRxDialog;
